// MACHINERY V2 (Stroje) — the three rail controls the fleet rail does not have
// (M2.8): a search box, a min–max range row, and a coloured-dot toggle row.
//
// Spec: data package §11 filter table. Artboards 0900 / 0901 draw all three.
// The ROW and SECTION atoms are imported from fleet/FilterRail.tsx rather than
// restated — see the export comment there. These three are additions, not
// variants, so they live with the surface that needs them.
//
// >>> THE RANGE INPUTS ARE UNCONTROLLED-BY-VALUE ON PURPOSE. <<<
// `value={query.minRpm ?? ""}` (v1, CatalogBrowsePage.tsx:784) makes the field
// a mirror of the PARSED number, so typing "1e" or a lone "-" is erased under
// the cursor and a trailing "." in an HP value cannot be typed at all. Here the
// input keeps its own raw string and only PUBLISHES a parsed number, which is
// what `parseRangeInt` / `parseRangeFloat` are for. The raw string is re-seeded
// from the query when the query changes from OUTSIDE (Clear all, or a fresh
// mount) — tracked against the raw text this input last published, so an
// in-flight edit is never clobbered by its own echo.
//
// >>> AND THEY PUBLISH ON A DEBOUNCE, NOT ON EVERY KEYSTROKE. <<<
// Publishing per keystroke wrote a new `query` object per character, and the
// browse hook's load effect depends on `query` — so typing "12000" into the
// RPM-min box dispatched FIVE overlapping `browseCatalogModels`, one per
// character, each for a filter the user was still in the middle of typing. The
// four throwaway requests are the network cost; the real hazard is that they
// can resolve out of order (see useCatalogBrowse.ts's stale-response guard,
// which is the other half of this fix). One publish per pause fixes the cause;
// that guard makes the survivors safe. The pending publish is CANCELLED when an
// outside change re-seeds the box, or "Clear all" would be undone 350ms later
// by the keystroke that preceded it.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React, { useCallback, useEffect, useRef, useState } from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import { RD } from "../readiness/readinessPresentation";
import { CheckBox } from "../fleet/FilterRail";

const inputStyle: React.CSSProperties = {
  width: "100%",
  minWidth: 0,
  height: 30,
  padding: "0 8px",
  borderRadius: 6,
  border: "1px solid var(--border)",
  background: "var(--card)",
  color: "var(--ink)",
  fontFamily: "var(--font-mono)",
  fontSize: 11,
  outline: "none",
};

export const RailSearch: React.FC<{
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  ariaLabel: string;
}> = ({ value, onChange, placeholder, ariaLabel }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 6,
      height: 32,
      padding: "0 9px",
      borderRadius: 8,
      border: "1px solid var(--border)",
      background: "var(--card)",
      minWidth: 0,
    }}
  >
    <svg
      viewBox="0 0 12 12"
      width="11"
      height="11"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      style={{ color: "var(--faint)", flexShrink: 0 }}
      aria-hidden
    >
      <circle cx="5" cy="5" r="3.4" />
      <path d="M7.6 7.6 L10.5 10.5" strokeLinecap="round" />
    </svg>
    <input
      type="text"
      value={value}
      aria-label={ariaLabel}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      style={{
        flex: 1,
        minWidth: 0,
        border: 0,
        outline: "none",
        background: "transparent",
        color: "var(--ink)",
        fontFamily: "inherit",
        fontSize: 12,
      }}
    />
  </div>
);

interface RangeInputProps {
  value?: number;
  onChange: (raw: string) => void;
  placeholder: string;
  ariaLabel: string;
}

/** One publish per pause. Long enough to swallow a five-digit RPM typed at
 *  speed, short enough that the grid still feels answerable to the box. */
const RANGE_PUBLISH_DEBOUNCE_MS = 350;

const RangeInput: React.FC<RangeInputProps> = ({
  value,
  onChange,
  placeholder,
  ariaLabel,
}) => {
  const [raw, setRaw] = useState(value === undefined ? "" : String(value));
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** The raw TEXT of the last publish. The re-seed test below is made against
   *  THIS, not against the box's current text: with a debounce the box can
   *  legitimately hold newer characters than the value now arriving back, and
   *  comparing against the current text would clobber them. */
  const publishedRef = useRef<string | null>(null);

  const cancelPending = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
  }, []);

  useEffect(() => () => cancelPending(), [cancelPending]);

  // Re-seed ONLY when the arriving value is NOT this input's own echo — i.e.
  // it came from outside (Clear all, a fresh mount, a category reset). A user
  // mid-edit gets back exactly what they published, so this does not fire and
  // the raw text (incl. a trailing ".") survives.
  useEffect(() => {
    const published = publishedRef.current;
    if (published !== null) {
      const parsed = published.trim() === "" ? undefined : Number(published);
      const isEcho = value === undefined ? published.trim() === "" : parsed === value;
      if (isEcho) return;
    }
    cancelPending();
    publishedRef.current = null;
    setRaw(value === undefined ? "" : String(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <input
      type="number"
      inputMode="decimal"
      value={raw}
      aria-label={ariaLabel}
      placeholder={placeholder}
      onChange={(e) => {
        const next = e.target.value;
        setRaw(next);
        cancelPending();
        timerRef.current = setTimeout(() => {
          timerRef.current = null;
          publishedRef.current = next;
          onChange(next);
        }, RANGE_PUBLISH_DEBOUNCE_MS);
      }}
      style={inputStyle}
    />
  );
};

/** The min–max pair the artboard draws for OTÁČKY VRETENA and VÝKON VRETENA,
 *  and which D6 reuses verbatim for the tool count. */
export const RailRangeRow: React.FC<{
  title: string;
  min?: number;
  max?: number;
  onMin: (raw: string) => void;
  onMax: (raw: string) => void;
}> = ({ title, min, max, onMin, onMax }) => {
  const { translate } = useTranslation("myMachinery");
  const minLabel = translate("myMachinery:min");
  const maxLabel = translate("myMachinery:max");

  return (
    <div>
      <div className="eyebrow" style={{ marginBottom: 6, padding: "0 4px" }}>
        {title}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 7, minWidth: 0 }}>
        <RangeInput
          value={min}
          onChange={onMin}
          placeholder={minLabel}
          ariaLabel={`${title} ${minLabel}`}
        />
        <span style={{ color: "var(--faint)", fontSize: 11, flexShrink: 0 }} aria-hidden>
          –
        </span>
        <RangeInput
          value={max}
          onChange={onMax}
          placeholder={maxLabel}
          ariaLabel={`${title} ${maxLabel}`}
        />
      </div>
    </div>
  );
};

/** The MIDAS-readiness checkboxes: a checkbox, a pinned signal dot (gold OEM /
 *  MIDAS blue — cross-palette constants from `RD`, never palette tokens), and a
 *  label. Built on the fleet rail's CheckBox so the tick is the same tick. */
export const RailToggleRow: React.FC<{
  label: string;
  dot: "oem" | "midas";
  active: boolean;
  onClick: () => void;
}> = ({ label, dot, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    style={{
      width: "100%",
      padding: "6px 8px",
      borderRadius: 6,
      background: active ? "var(--mod-accent-soft)" : "transparent",
      border: 0,
      borderLeft: "2px solid " + (active ? "var(--mod-accent)" : "transparent"),
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: 8,
      fontFamily: "inherit",
      fontSize: 13,
      textAlign: "left",
      color: active ? "var(--mod-accent)" : "var(--ink-2)",
      fontWeight: active ? 600 : 500,
      minWidth: 0,
    }}
  >
    <CheckBox checked={active} />
    <span
      aria-hidden
      style={{
        width: 7,
        height: 7,
        borderRadius: 999,
        background: dot === "oem" ? RD.oem : RD.midas,
        flexShrink: 0,
      }}
    />
    <span
      style={{
        flex: 1,
        minWidth: 0,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      {label}
    </span>
  </button>
);
